import React from 'react';
import {
    Alert,
    Box,
    Chip,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material';
import {Link as RouterLink} from 'react-router';
import TabEmpty from './TabEmpty';
import TabLoading from './TabLoading';

function itemLabel(item) {
    return item?.title
        || item?.icrNumber
        || item?.eeiNumber
        || item?.gapNumber
        || item?.ctNumber
        || item?.cmNumber
        || item?.iirNumber
        || item?.id
        || 'Untitled';
}

function itemMeta(item) {
    if (item?.icrNumber) return item.icrNumber;
    if (item?.eeiNumber) return item.eeiNumber;
    if (item?.gapNumber) return item.gapNumber;
    if (item?.ctNumber) return item.ctNumber;
    if (item?.cmNumber) return item.cmNumber;
    if (item?.iirNumber) return item.iirNumber;
    return null;
}

function RequirementList({items}) {
    if (!items.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                None
            </Typography>
        );
    }

    return (
        <Stack spacing={0.75}>
            {items.map((item, idx) => {
                const key = item.id || item.href || `${idx}`;
                const title = itemLabel(item);
                const meta = itemMeta(item);
                const showMeta = meta && meta !== title;
                const linkBody = (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" component="span">
                            {title}
                        </Typography>
                        {showMeta && (
                            <Typography variant="caption" color="text.secondary" component="span">
                                {meta}
                            </Typography>
                        )}
                        {item.status && (
                            <Chip size="small" label={item.status} variant="outlined" sx={{height: 22}}/>
                        )}
                    </Stack>
                );

                if (item.href) {
                    return (
                        <MuiLink
                            key={key}
                            component={RouterLink}
                            to={item.href}
                            underline="hover"
                            color="inherit"
                        >
                            {linkBody}
                        </MuiLink>
                    );
                }

                return (
                    <Box key={key}>{linkBody}</Box>
                );
            })}
        </Stack>
    );
}

function RequirementBlock({title, items}) {
    return (
        <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
                {title} ({items.length})
            </Typography>
            <RequirementList items={items}/>
        </Stack>
    );
}

const EMPTY_MESSAGE =
    'This entity is not linked to any ICRs, EEIs, Gaps, Collection Tasks, Collection Materials, or IIRs.';

/**
 * Collection tab — requirements and products linked to this KB entity.
 */
const CollectionRequirementsTab = ({
    icrs = [],
    eeis = [],
    gaps = [],
    cts = [],
    cms = [],
    iirs = [],
    loading,
    error = null,
}) => {
    if (loading) return <TabLoading/>;

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">
                    {error.message || 'Failed to load collection requirements.'}
                </Alert>
            </Box>
        );
    }

    const total = icrs.length + eeis.length + gaps.length + cts.length + cms.length + iirs.length;
    if (!total) {
        return <TabEmpty message={EMPTY_MESSAGE}/>;
    }

    return (
        <Stack spacing={3} px={2} py={2}>
            <RequirementBlock title="ICRs" items={icrs}/>
            <RequirementBlock title="EEIs" items={eeis}/>
            <RequirementBlock title="Gaps" items={gaps}/>
            <RequirementBlock title="Collection Tasks" items={cts}/>
            <RequirementBlock title="Collection Materials" items={cms}/>
            <RequirementBlock title="IIRs" items={iirs}/>
        </Stack>
    );
};

export default CollectionRequirementsTab;
